import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { QuickReplySetList, SendDatasetModal } from '../components/quick-replies';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const QuickRepliesListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { currentBusiness } = useAppStore();
  const [quickReplySets, setQuickReplySets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [setToDelete, setSetToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadQuickReplySets = useCallback(async (page: number = 0, reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
        setError(null);
      }

      const response = await apiService.getQuickReplySets({
        limit: 20,
        skip: page * 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (response.statusCode === 1000 && response.data) {
        const newSets = response.data.quickReplySets || [];
        setQuickReplySets((prev) => (reset ? newSets : [...prev, ...newSets]));
        setTotal(response.data.pagination?.total || 0);
        setHasMore(newSets.length === 20);
        setCurrentPage(page);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load quick reply sets';
        setError(errorMessage);
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error loading quick reply sets:', error);
      const errorMessage = error.message || 'Failed to load quick reply sets';
      setError(errorMessage);
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadQuickReplySets(0, true);
  }, [loadQuickReplySets]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadQuickReplySets(0, true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore && !error) {
      loadQuickReplySets(currentPage + 1, false);
    }
  };

  const handleQuickReplySetPress = (setId: string) => {
    // Navigate to edit screen (detail screen to be implemented in Part 2)
    navigateTo('quick-reply-set-edit', setId);
  };

  const handleQuickReplySetEdit = (setId: string) => {
    navigateTo('quick-reply-set-edit', setId);
  };

  const handleQuickReplySetManageItems = (setId: string) => {
    navigateTo('quick-reply-set-items', setId);
  };

  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedSetIdForSend, setSelectedSetIdForSend] = useState<string | null>(null);

  const handleQuickReplySetSend = (setId: string) => {
    setSelectedSetIdForSend(setId);
    setShowSendModal(true);
  };

  const handleQuickReplySetDelete = (setId: string) => {
    setSetToDelete(setId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!setToDelete) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteQuickReplySet(setToDelete);

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Quick reply set deleted successfully.');
        setQuickReplySets((prev) => prev.filter((set) => set._id !== setToDelete));
        setShowDeleteModal(false);
        setSetToDelete(null);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete quick reply set';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error deleting quick reply set:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete quick reply set');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = () => {
    navigateTo('quick-reply-set-create');
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerContainer}>
            <View style={styles.headerRow}>
              <TouchableOpacity
                style={styles.backButton}
                onPress={() => navigateTo('home')}
              >
                <DirectionalIcon name="arrow-back" size={24} color={colors.text.primary} />
              </TouchableOpacity>
              <View style={styles.titleContainer}>
                <View style={styles.titleRow}>
                  <Text style={styles.title}>Reply Sets</Text>
                  <TouchableOpacity
                    style={styles.replyItemsButton}
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      console.log('🎯 [QuickRepliesListScreen] Button clicked - Navigating to quick-reply-items');
                      try {
                        navigateTo('quick-reply-items');
                      } catch (error) {
                        console.error('🎯 [QuickRepliesListScreen] Navigation error:', error);
                      }
                    }}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="list-outline" size={18} color={colors.primary} />
                    <Text style={styles.replyItemsButtonText}>Reply Items</Text>
                  </TouchableOpacity>
                </View>
                <Text style={styles.subtitle}>
                  Create and manage reply sets for your channels
                </Text>
              </View>
              <View style={styles.headerActions}>
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={handleCreate}
                  activeOpacity={0.8}
                >
                  <Ionicons name="add" size={24} color={colors.white} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

            <QuickReplySetList
              quickReplySets={quickReplySets}
              loading={loading}
              refreshing={refreshing}
              onRefresh={handleRefresh}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              onQuickReplySetPress={handleQuickReplySetPress}
              onQuickReplySetEdit={handleQuickReplySetEdit}
              onQuickReplySetDelete={handleQuickReplySetDelete}
              onQuickReplySetManageItems={handleQuickReplySetManageItems}
              onQuickReplySetSend={handleQuickReplySetSend}
              showActions={true}
            />

        <ConfirmationModal
          visible={showDeleteModal}
          title="Delete Reply Set"
          message="Are you sure you want to delete this quick reply set? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSetToDelete(null);
          }}
          confirmButtonColor={colors.error}
          loading={deleting}
        />

        <SendDatasetModal
          visible={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSelectedSetIdForSend(null);
          }}
          onMessageSent={() => {
            setShowSendModal(false);
            setSelectedSetIdForSend(null);
            handleRefresh();
          }}
          defaultSetId={selectedSetIdForSend || undefined}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '40',
  },
  headerContainer: {
    padding: spacing.lg,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: spacing.xs,
    marginRight: spacing.md,
  },
  titleContainer: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  replyItemsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary + '15',
    borderWidth: 1,
    borderColor: colors.primary + '40',
  },
  replyItemsButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
});


